/**
 * Per-hub POSITIONS — where the user was on each hub (last room, last settings
 * page, last dashboard, last cross-section path), stored PER CONNECTION and
 * read back only for that same connection.
 *
 * Owner report (card `t_e9763f84`): "rooms menu with last selected, should be
 * saved per hub.. now you go to different hub and it tries to open a room which
 * exists only on previously connected home."
 *
 * The cause was the key list, not the readers. Every WRITER walked
 * `getHubSessionKeys()`, which deliberately also yields `otto_active_hub_key` —
 * the hub being LEFT — plus the endpoint the runtime is pointed at right now.
 * Opening a room on hub B while `otto_active_hub_key` was still hub A therefore
 * wrote B's room id under `lastViewedRoomsPage:hub:A`; coming back to A the
 * reader correctly read `hub:A` and got a room that only exists on B.
 *
 * The rule lives here, once: a position belongs to exactly the connection named
 * by the arguments — `getHubOwnKeys()`. Nothing in this module may add a key
 * that could belong to another hub. (`getHubSessionKeys()` keeps its permissive
 * behaviour for CREDENTIALS and device tokens, where finding *something* is the
 * whole point — see `services/deviceCredential.ts` and the docstring at
 * `config/runtime.ts:237-247`.)
 *
 * A stored position also OUTLIVES the hub it was stored on: a value written by
 * an older build — or by this very bug — can be foreign to the connection
 * reading it now. `resolveRoomsPosition()` is how a room id survives that: it
 * is validated against the hub being visited before the room is opened, so a
 * stale foreign id falls back to the rooms overview instead of opening (or
 * bouncing off) a room that is not there.
 */
import { getHubOwnKeys, getRuntimeConfig } from '../config/runtime';

/** The positions the app remembers, one storage prefix each. */
export const LAST_VIEWED_SETTINGS_PAGE = 'lastViewedSettingsPage';
export const LAST_VIEWED_ROOMS_PAGE = 'lastViewedRoomsPage';
export const LAST_VIEWED_DASHBOARD = 'lastViewedDashboard';
export const LAST_HUB_PATH = 'lastHubPath';

export type HubPosition =
  | typeof LAST_VIEWED_SETTINGS_PAGE
  | typeof LAST_VIEWED_ROOMS_PAGE
  | typeof LAST_VIEWED_DASHBOARD
  | typeof LAST_HUB_PATH;

/**
 * The connection a position belongs to: a cloud hub id when the route names one
 * (`/h/:hubId/...`), otherwise the LAN/Electron-local endpoint this client is
 * pointed at. Never both — see `getHubOwnKeys()`.
 */
export interface HubPositionConnection {
  hubId?: string | string[] | null;
  apiUrl?: string | null;
}

/**
 * The connection a navigation belongs to, for every per-hub POSITION: the hub
 * id the route names, else the LAN/Electron endpoint this client is pointed at.
 * Reading the runtime config can throw before it is loaded (the very first
 * navigation) — the hub id alone still identifies a cloud route, and a LAN
 * route without it simply matches nothing.
 */
export function visitedConnection(hubId?: string | string[] | null): HubPositionConnection {
  let apiUrl: string | null = null;
  try {
    apiUrl = getRuntimeConfig().apiUrl;
  } catch {
    // Config not loaded yet — `getHubOwnKeys()` falls back on its own.
  }
  return { hubId, apiUrl };
}

/** The slice of Storage this module needs — injectable so it is testable. */
export interface HubPositionStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function defaultStorage(): HubPositionStorage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

/** The storage keys this connection's `position` lives under, most specific first. */
export function hubPositionKeys(position: HubPosition, connection: HubPositionConnection = {}): string[] {
  return getHubOwnKeys(connection).map((key) => `${position}:${key}`);
}

/**
 * Store `position` for this connection. Returns the keys written, so a caller
 * (or a test) can see that no other hub's key was touched.
 */
export function writeHubPosition(
  position: HubPosition,
  value: string,
  connection: HubPositionConnection = {},
  storage?: HubPositionStorage | null,
): string[] {
  const store = storage === undefined ? defaultStorage() : storage;
  if (!store) return [];
  const keys = hubPositionKeys(position, connection);
  for (const key of keys) {
    try {
      store.setItem(key, value);
    } catch {
      // Unwritable storage — the position is simply not remembered.
    }
  }
  return keys;
}

/** This connection's stored `position`, or null. Never another connection's. */
export function readHubPosition(
  position: HubPosition,
  connection: HubPositionConnection = {},
  storage?: HubPositionStorage | null,
): string | null {
  const store = storage === undefined ? defaultStorage() : storage;
  if (!store) return null;
  for (const key of hubPositionKeys(position, connection)) {
    try {
      const value = store.getItem(key);
      if (value) return value;
    } catch {
      // Unreadable entry — try the next key instead of failing the restore.
    }
  }
  return null;
}

/**
 * A rooms sub-path that names a ROOM (as opposed to the section's own
 * `overview`, or a `_redirect` sentinel). Mirrors the shape check
 * `ServersSection.navigateToLocalApp()` has always used for a stored room id,
 * plus the `_`-prefixed sentinel rule the rest of the app routes use.
 */
export function isRoomSegment(segment: string): boolean {
  return segment !== 'overview' && !segment.startsWith('_') && /^[a-zA-Z0-9_-]+$/.test(segment);
}

/**
 * Did the hub answer "I have no such room"? A hub that does not have the room
 * answers 404; nothing else means the room is absent (a 401 or a dropped
 * connection says nothing about the room).
 */
export function isMissingRoom(error: unknown): boolean {
  return (error as { status?: number } | null | undefined)?.status === 404;
}

/**
 * `true` — the hub being visited has the room; `false` — it answered 404;
 * `null` — the probe could not tell (offline, refused, not authenticated).
 */
export type RoomProbe = (roomId: string) => Promise<boolean | null>;

/**
 * The rooms sub-path to open for a connection. `overview` and anything that is
 * not a room id pass through; a stored room id is only opened when the hub
 * being visited still answers for it, so a value left behind by another hub
 * falls back to `overview` instead of opening a room that is not there.
 *
 * An inconclusive probe (`null`) keeps the stored value: a network hiccup must
 * not throw a good position away, and the room page itself answers a real 404.
 */
export async function resolveRoomsPosition(stored: string | null | undefined, roomExists: RoomProbe): Promise<string> {
  const value = String(stored ?? '').trim();
  if (!value) return 'overview';
  if (!isRoomSegment(value)) return value;
  return (await roomExists(value)) === false ? 'overview' : value;
}
